import { getCarLocation } from "./reservationFormatters";

/* Filter keys shared by the desktop table and the mobile list. */
export const RESERVATION_FILTER_KEYS = {
    SEARCH: "q",
    ID: "id",
    CUSTOMER: "customerName",
    CAR_TYPE: "carType",
    VIN: "vin",
    LOCATION: "location",
    ASSIGNMENT: "assignment",
};

// Upcoming-tab "car assignment" filter, applied by the API (see fetchAllReservationsForTab).
export const getAssignmentOptions = (t) => [
    { value: "all", label: t("quickReservations.regularReservations") },
    { value: "car_needed", label: t("quickReservations.carNeeded") },
    { value: "car_assigned", label: t("quickReservations.carAssigned") },
    { value: "quick_reservation", label: t("quickReservations.title") },
];

// Labels/values for the "Filtered by" chips, shared by the desktop table and the mobile list.
export const getReservationFilterChipProps = (t) => {
    const assignmentOptions = getAssignmentOptions(t);
    return {
        filterLabels: {
            [RESERVATION_FILTER_KEYS.ID]: t("reservationID"),
            [RESERVATION_FILTER_KEYS.CUSTOMER]: t("customer"),
            [RESERVATION_FILTER_KEYS.CAR_TYPE]: t("iadvreq.carType"),
            [RESERVATION_FILTER_KEYS.VIN]: t("vin"),
            [RESERVATION_FILTER_KEYS.LOCATION]: t("location"),
            [RESERVATION_FILTER_KEYS.ASSIGNMENT]: t("carAssignment"),
        },
        formatFilterValue: (key, value) =>
            key === RESERVATION_FILTER_KEYS.ASSIGNMENT
                ? assignmentOptions.find((option) => option.value === value)?.label
                : undefined,
    };
};

const normalize = (value) => (value ?? "").toString().toLowerCase();
const digitsOnly = (value) => (value ?? "").toString().replace(/\D/g, "");

const fieldReaders = {
    [RESERVATION_FILTER_KEYS.ID]: (r) => [r.id],
    [RESERVATION_FILTER_KEYS.CUSTOMER]: (r) => [r.customerName, r.firstName, r.lastName, r.email, r.mobileNumber],
    [RESERVATION_FILTER_KEYS.CAR_TYPE]: (r) => [r.carType, r.carName, r.carSelected?.carName],
    [RESERVATION_FILTER_KEYS.VIN]: (r) => [r.carSelected?.VIN],
    [RESERVATION_FILTER_KEYS.LOCATION]: (r) => [getCarLocation(r)],
};

// Everything the toolbar search box looks at, including the plate number.
const readSearchableFields = (r) => [
    ...Object.values(fieldReaders).flatMap((read) => read(r)),
    r.carSelected?.carNumber,
];

const PHONE_LIKE = /^[\d\s()+.-]+$/;

const matchesText = (values, query) => {
    const needle = normalize(query).trim();
    if (!needle) return true;
    return values.some((value) => normalize(value).includes(needle));
};

// Lets "(770) 678" match a phone stored as "7706780555".
const matchesPhone = (reservation, query) => {
    const needle = (query ?? "").toString().trim();
    if (!PHONE_LIKE.test(needle)) return false;
    const needleDigits = digitsOnly(needle);
    return needleDigits.length >= 3 && digitsOnly(reservation.mobileNumber).includes(needleDigits);
};

/**
 * Client-side filtering for reservation lists. The list endpoints only do exact matching on
 * some fields (and `searchTerm` errors on /api/reservation/list), so text search happens here.
 * The ASSIGNMENT filter is applied server-side and ignored by this function.
 */
export const filterReservations = (rows, filters = {}) => {
    const active = Object.entries(filters).filter(
        ([key, value]) => key !== RESERVATION_FILTER_KEYS.ASSIGNMENT && value,
    );
    if (active.length === 0) return rows;

    return rows.filter((reservation) =>
        active.every(([key, value]) => {
            if (key === RESERVATION_FILTER_KEYS.SEARCH) {
                return matchesText(readSearchableFields(reservation), value) || matchesPhone(reservation, value);
            }
            if (key === RESERVATION_FILTER_KEYS.CUSTOMER && matchesPhone(reservation, value)) {
                return true;
            }
            const read = fieldReaders[key];
            return read ? matchesText(read(reservation), value) : true;
        }),
    );
};
